import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import useTranslation from 'next-translate/useTranslation';

import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Rectangle,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';

//hook
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';

import { SizeMe } from '@/components/base';
// Components
import TooltipCustom from './components/TooltipCustom';

import ChartWrapper from './ChartWrapper';

dayjs.extend(utc);
dayjs.extend(timezone);

const DynamicTableChart = dynamic(() => import('./components/TableChart'));

const CustomHourAxisTick = ({
    x,
    y,
    payload,
    userNumbersFormat,
    timeFormat,
    dateFormat,
    isExport
}) => {
    const { t } = useTranslation('analytics');
    const { formatDateTimeNoTz } = useFormatDateTime(userNumbersFormat?.date?.timezone);
    let value;

    if (timeFormat) {
        value = timeFormat(payload.value);
    } else if (dateFormat) {
        value = formatDateTimeNoTz(payload.value, userNumbersFormat?.date?.format);
    } else {
        value = t(payload.value);
    }

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={7}
                y={7}
                dy={0}
                textAnchor="end"
                transform="rotate(-45)"
                className={clsx('fill-current font-GSemiBold text-xxs text-icoGray-500', {
                    'dark:text-icoDarkMode-wolf': !isExport
                })}>
                {value}
            </text>
        </g>
    );
};

CustomHourAxisTick.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    payload: PropTypes.object
};

const BarGroupedChartComponent = ({
    data,
    columnNames,
    activeTable,
    userNumbersFormat,
    isExport,
    hiddenKeys,
    height = 230
}) => {
    const { t } = useTranslation('analytics');
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';
    const isDateChart = useMemo(() => Object.keys(data.name)[0] === 'date', [data]);
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { formatDateTimeNoTz } = useFormatDateTime(userNumbersFormat?.date?.timezone);

    const shouldAnimate = useMemo(() => {
        if (isExport) return false;
        return data?.graphData?.length < 50;
    }, [data?.graphData?.length, isExport]);

    return (
        <>
            {activeTable ? (
                <DynamicTableChart
                    isExport={isExport}
                    graphData={data.graphData}
                    columnNames={columnNames}
                    valueFormat={data.valueFormat.notation !== 'compact' ? data.valueFormat : {}}
                    timeFormat={data.timeFormatter}
                    userNumbersFormat={userNumbersFormat}
                    formatterTranslation={data?.tableColumnFormatterTranslation}
                />
            ) : (
                <SizeMe>
                    {({ size }) => (
                        <div
                            className="relative h-full w-full overflow-x-auto"
                            style={{ minHeight: isExport ? 180 : height }}>
                            <div className="absolute left-0 top-0 h-full w-full">
                                <ResponsiveContainer
                                    height={isExport ? 180 : height}
                                    width={isExport ? data.fixedSize : '100%'}
                                    minWidth={data.minWidth}>
                                    <BarChart
                                        data={data.graphData}
                                        barSize={data.barSize ?? 3}
                                        barGap={data.barGap ?? 2}
                                        margin={data.margin}>
                                        <CartesianGrid
                                            vertical={false}
                                            stroke={darkModeColorsChart[theme].grid}
                                        />
                                        <YAxis
                                            tickLine={false}
                                            axisLine={false}
                                            tickCount={7}
                                            allowDecimals={data?.yAxisAllowDecimal ?? true}
                                            tickFormatter={(item) =>
                                                formatNumber(item, data.valueFormat)
                                            }
                                        />
                                        <XAxis
                                            dataKey={Object.values(data.name)[0]}
                                            type="category"
                                            interval={
                                                // responsive xAxis labels
                                                data.graphData.length * 20 > size.width
                                                    ? Math.round(
                                                          data.graphData.length / (size.width / 20)
                                                      )
                                                    : 0
                                            }
                                            tick={
                                                data.graphData.length > 8 ? (
                                                    <CustomHourAxisTick
                                                        userNumbersFormat={userNumbersFormat}
                                                        timeFormat={data.timeFormatter}
                                                        dateFormat={isDateChart}
                                                    />
                                                ) : (
                                                    {}
                                                )
                                            }
                                            tickFormatter={
                                                data.graphData.length <= 8
                                                    ? (item) => {
                                                          if (isDateChart) {
                                                              return formatDateTimeNoTz(
                                                                  item,
                                                                  userNumbersFormat?.date?.format
                                                              );
                                                          }
                                                          if (data?.tickFormatterTranslation) {
                                                              return t(
                                                                  `${data?.tickFormatterTranslation}.${item}`
                                                              );
                                                          }
                                                          if (data.timeFormatter) {
                                                              return data.timeFormatter(item);
                                                          } else {
                                                              return t(item);
                                                          }
                                                      }
                                                    : null
                                            }
                                            minTickGap={isDateChart && -60}
                                            tickMargin={10}
                                            tickLine={false}
                                            axisLine={false}
                                        />
                                        <Tooltip
                                            cursor={false}
                                            content={
                                                <TooltipCustom
                                                    data={data}
                                                    valueFormat={
                                                        data.valueFormat.notation !== 'compact'
                                                            ? data.valueFormat
                                                            : {}
                                                    }
                                                    timeFormat={data.timeFormatter}
                                                    tooltipTranslation={data.tooltipTranslation}
                                                />
                                            }
                                        />
                                        {Object.entries(data.graphData[0]).map((item, index) => {
                                            if (
                                                item[0] !== Object.values(data.name)[0] &&
                                                !hiddenKeys?.[item[0]]
                                            ) {
                                                return (
                                                    <Bar
                                                        key={index}
                                                        isAnimationActive={shouldAnimate}
                                                        animationDuration={300}
                                                        name={t(data.name[item[0]])}
                                                        fill={
                                                            darkModeColorsChart[theme][
                                                                data.color[item[0]]
                                                            ]
                                                        }
                                                        dataKey={item[0]}
                                                        stackId={data.isStacked ? 'stack' : null}
                                                        shape={(props) => {
                                                            data.isStacked &&
                                                            index !==
                                                                Object.entries(data.graphData[0])
                                                                    .length -
                                                                    1
                                                                ? (props.radius = [0, 0, 0, 0])
                                                                : (props.radius = [
                                                                      data.barRadius ?? 10,
                                                                      data.barRadius ?? 10,
                                                                      0,
                                                                      0
                                                                  ]);

                                                            return <Rectangle {...props} />;
                                                        }}
                                                    />
                                                );
                                            }
                                        })}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </SizeMe>
            )}
        </>
    );
};

BarGroupedChartComponent.propTypes = {
    data: PropTypes.object,
    columnNames: PropTypes.object,
    activeTable: PropTypes.bool,
    height: PropTypes.number,
    userNumbersFormat: PropTypes.object,
    isExport: PropTypes.bool,
    hiddenKeys: PropTypes.object
};

const BarGroupedChart = ({
    data,
    onReset = () => {
        /** */
    },
    isLoading = false,
    className = '',
    ...restProps
}) => (
    <ChartWrapper
        className={className}
        isLoading={isLoading}
        onReset={onReset}
        data={data}
        legendType="square"
        {...restProps}>
        <BarGroupedChartComponent />
    </ChartWrapper>
);

BarGroupedChart.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool
};

export { BarGroupedChart, BarGroupedChartComponent };
